import {
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/20/solid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import Button from 'components/inputs/button';
import Multiselect from 'components/inputs/multiselect';
import Select from 'components/inputs/select';
import Text from 'components/inputs/text';
import Textarea from 'components/inputs/textarea';
import { SystemDesignRuleType } from 'data/types';
import { fetchDefaultAdminData } from 'features/admin';
import { useEffect, useMemo, useState } from 'react';
import axios from 'utils/api';
import { confirmationModal, errorModal, successModal } from 'utils/ui';

const SEVERITY_OPTIONS = ['Error', 'Warning', 'Advisory'];

const EMPTY_RULE: Omit<SystemDesignRuleType, 'id'> = {
  code: '',
  name: '',
  category: '',
  summary: '',
  reasoning: '',
  message: '',
  triggers: '',
  severity: 'Warning',
  active: false,
  steps: [],
  recommendations: []
};

export default function DesignRulesPage() {
  const dispatch = useAppDispatch();
  const { designRules, designRuleStepOptions, designRuleRecommendationTypes } =
    useAppSelector((state) => state.admin);

  const [view, setView] = useState<'table' | 'detail'>('table');
  const [selectedRuleId, setSelectedRuleId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [stepFilter, setStepFilter] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<string>('');

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [severity, setSeverity] = useState('Warning');
  const [summary, setSummary] = useState('');
  const [reasoning, setReasoning] = useState('');
  const [message, setMessage] = useState('');
  const [triggers, setTriggers] = useState('');
  const [active, setActive] = useState(false);
  const [steps, setSteps] = useState<string[]>([]);
  const [recommendations, setRecommendations] = useState<
    {
      recommendation: string;
      aiInstructions: string;
      active: boolean;
      typeId: number;
    }[]
  >([]);

  const categories = useMemo(
    () => [...new Set(designRules.map((r) => r.category))].sort(),
    [designRules]
  );

  const filteredRules = useMemo(() => {
    let rules = designRules;
    if (categoryFilter) {
      rules = rules.filter((r) => r.category === categoryFilter);
    }
    if (stepFilter) {
      rules = rules.filter((r) => r.steps.some((s) => s.step === stepFilter));
    }
    if (activeFilter === 'active') {
      rules = rules.filter((r) => r.active);
    } else if (activeFilter === 'inactive') {
      rules = rules.filter((r) => !r.active);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      rules = rules.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          r.message.toLowerCase().includes(q) ||
          r.category.toLowerCase().includes(q)
      );
    }
    return rules;
  }, [designRules, categoryFilter, stepFilter, activeFilter, searchQuery]);

  const selectedRule = designRules.find((r) => r.id === selectedRuleId) ?? null;

  const loadRule = (rule: SystemDesignRuleType | null) => {
    if (rule) {
      setCode(rule.code);
      setName(rule.name);
      setCategory(rule.category);
      setSeverity(rule.severity);
      setActive(rule.active);
      setSummary(rule.summary);
      setReasoning(rule.reasoning);
      setMessage(rule.message);
      setTriggers(rule.triggers);
      setSteps(rule.steps.map((s) => s.step));
      setRecommendations(
        rule.recommendations.map((r) => ({
          recommendation: r.recommendation,
          aiInstructions: r.aiInstructions,
          active: r.active,
          typeId: r.typeId
        }))
      );
    } else {
      setCode(EMPTY_RULE.code);
      setName(EMPTY_RULE.name);
      setCategory(EMPTY_RULE.category);
      setSeverity(EMPTY_RULE.severity);
      setActive(EMPTY_RULE.active);
      setSummary(EMPTY_RULE.summary);
      setReasoning(EMPTY_RULE.reasoning);
      setMessage(EMPTY_RULE.message);
      setTriggers(EMPTY_RULE.triggers);
      setSteps([]);
      setRecommendations([]);
    }
  };

  useEffect(() => {
    if (isCreating) {
      loadRule(null);
    } else {
      loadRule(selectedRule);
    }
  }, [selectedRuleId, isCreating, designRules]);

  const handleSelectRule = (id: number) => {
    setIsCreating(false);
    setSelectedRuleId(id);
    setView('detail');
  };

  const handleNewRule = () => {
    setSelectedRuleId(null);
    setIsCreating(true);
    loadRule(null);
    setView('detail');
  };

  const handleBack = () => {
    setView('table');
    setSelectedRuleId(null);
    setIsCreating(false);
  };

  const handleSave = async () => {
    if (!code.trim() || !name.trim() || !category.trim() || !severity.trim()) {
      errorModal('Code, name, category, and severity are required.');
      return;
    }

    const payload = {
      code: code.trim(),
      name: name.trim(),
      category: category.trim(),
      summary: summary.trim(),
      reasoning: reasoning.trim(),
      message: message.trim(),
      triggers: triggers.trim(),
      severity,
      active,
      steps: steps.filter((s) => s.trim()),
      recommendations: recommendations.filter((r) => r.recommendation.trim())
    };

    try {
      if (isCreating) {
        await axios.post('design-rule', payload);
        dispatch(fetchDefaultAdminData());
        successModal('Design rule created.');
      } else if (selectedRuleId) {
        await axios.patch('design-rule', { id: selectedRuleId, ...payload });
        dispatch(fetchDefaultAdminData());
        successModal('Design rule updated.');
      }
      setIsCreating(false);
      setSelectedRuleId(null);
      setView('table');
    } catch {
      errorModal('Something went wrong.');
    }
  };

  const handleReset = () => {
    confirmationModal(
      'Are you sure you want to reset? All unsaved changes will be lost.',
      () => {
        if (isCreating) {
          loadRule(null);
        } else {
          loadRule(selectedRule);
        }
      }
    );
  };

  const handleDelete = () => {
    if (!selectedRuleId) return;
    confirmationModal(
      `Are you sure you want to delete "${selectedRule?.name}"? This cannot be undone.`,
      async () => {
        try {
          await axios.delete(`design-rule/${selectedRuleId}`);
          setSelectedRuleId(null);
          setView('table');
          dispatch(fetchDefaultAdminData());
          successModal('Design rule deleted.');
        } catch {
          errorModal('Something went wrong.');
        }
      }
    );
  };

  const updateRecommendation = (
    index: number,
    field: 'recommendation' | 'aiInstructions' | 'active' | 'typeId',
    value: string | boolean | number
  ) => {
    setRecommendations((prev) =>
      prev.map((r, i) => (i === index ? { ...r, [field]: value } : r))
    );
  };

  const removeRecommendation = (index: number) => {
    setRecommendations((prev) => prev.filter((_, i) => i !== index));
  };

  const severityBadge = (sev: string) => {
    const colors: Record<string, string> = {
      Error: 'bg-red-100 text-red-700',
      Warning: 'bg-yellow-100 text-yellow-800',
      Advisory: 'bg-blue-100 text-blue-700'
    };
    return (
      <span
        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
          colors[sev] ?? 'bg-gray-100 text-gray-700'
        }`}
      >
        {sev}
      </span>
    );
  };

  if (view === 'detail') {
    return (
      <div className='mx-auto flex max-w-[1600px] flex-col gap-5'>
        <div className='flex items-center gap-3'>
          <button
            type='button'
            onClick={handleBack}
            className='flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-700'
          >
            <ArrowLeftIcon className='h-4 w-4' />
            Back to Rules
          </button>
        </div>

        <div className='text-2xl font-bold text-gray-900'>
          {isCreating ? 'New Rule' : selectedRule?.name ?? 'Edit Rule'}
        </div>

        <div className='grid grid-cols-2 gap-4'>
          <div className='col-span-2 flex flex-col gap-1'>
            <label className='text-sm font-semibold text-gray-700'>Code</label>
            <Text
              value={code}
              onChange={(v) => setCode(v)}
              placeholder='e.g. wall-mounted-odu-weight-limit'
            />
          </div>

          <div className='col-span-2 flex flex-col gap-1'>
            <label className='text-sm font-semibold text-gray-700'>Name</label>
            <Text
              value={name}
              onChange={(v) => setName(v)}
              placeholder='Rule name'
            />
          </div>

          <div className='flex flex-col gap-1'>
            <label className='text-sm font-semibold text-gray-700'>
              Category
            </label>
            <Text
              value={category}
              onChange={(v) => setCategory(v)}
              placeholder='e.g. Equipment Compatibility'
            />
          </div>

          <div className='flex flex-col gap-1'>
            <label className='text-sm font-semibold text-gray-700'>
              Severity
            </label>
            <Select
              value={severity}
              onChange={(v) => setSeverity(v.toString())}
              options={SEVERITY_OPTIONS}
              preventDeselection
            />
          </div>
        </div>

        <div className='flex items-center gap-2'>
          <label className='relative inline-flex cursor-pointer items-center'>
            <input
              type='checkbox'
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className='peer sr-only'
            />
            <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-green-500 peer-checked:after:translate-x-full peer-checked:after:border-white" />
          </label>
          <span className='text-sm font-semibold text-gray-700'>
            {active ? 'Active' : 'Inactive'}
          </span>
        </div>

        <div className='flex flex-col gap-1'>
          <label className='text-sm font-semibold text-gray-700'>Message</label>
          <Textarea
            value={message}
            onChange={(v) => setMessage(v)}
            placeholder='User-facing message'
            rows={2}
          />
        </div>

        <div className='flex flex-col gap-1'>
          <label className='text-sm font-semibold text-gray-700'>Summary</label>
          <Textarea
            value={summary}
            onChange={(v) => setSummary(v)}
            placeholder='Rule summary'
            rows={2}
          />
        </div>

        <div className='flex flex-col gap-1'>
          <label className='text-sm font-semibold text-gray-700'>
            Reasoning
          </label>
          <Textarea
            value={reasoning}
            onChange={(v) => setReasoning(v)}
            placeholder='Why this matters'
            rows={2}
          />
        </div>

        <div className='flex flex-col gap-1'>
          <label className='text-sm font-semibold text-gray-700'>
            Triggers
          </label>
          <Textarea
            value={triggers}
            onChange={(v) => setTriggers(v)}
            placeholder='Conditions that trigger this rule'
            rows={3}
          />
        </div>

        <div className='flex flex-col gap-1'>
          <label className='text-sm font-semibold text-gray-700'>Steps</label>
          <Multiselect
            value={steps}
            options={designRuleStepOptions}
            onChange={(values) => setSteps(values as string[])}
            placeholder='Select steps...'
          />
        </div>

        <div className='flex flex-col gap-2'>
          <label className='text-sm font-semibold text-gray-700'>
            Recommended Fixes
          </label>
          {recommendations.map((rec, i) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              className='flex flex-col gap-2 rounded border border-gray-200 p-2'
            >
              <div className='flex items-center gap-2'>
                <div className='grow'>
                  <Text
                    value={rec.recommendation}
                    onChange={(v) =>
                      updateRecommendation(i, 'recommendation', v)
                    }
                    placeholder='Recommendation'
                  />
                </div>
                <button
                  type='button'
                  onClick={() => removeRecommendation(i)}
                  className='shrink-0 text-gray-400 hover:text-red-500'
                >
                  <TrashIcon className='h-4 w-4' />
                </button>
              </div>
              <div className='flex items-center gap-4'>
                <div className='flex items-center gap-2'>
                  <label className='relative inline-flex cursor-pointer items-center'>
                    <input
                      type='checkbox'
                      checked={rec.active}
                      onChange={(e) =>
                        updateRecommendation(i, 'active', e.target.checked)
                      }
                      className='peer sr-only'
                    />
                    <div className="peer h-5 w-9 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-green-500 peer-checked:after:translate-x-full peer-checked:after:border-white" />
                  </label>
                  <span className='text-xs font-medium text-gray-600'>
                    {rec.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className='w-40'>
                  <Select
                    value={rec.typeId}
                    onChange={(v) =>
                      updateRecommendation(i, 'typeId', Number(v))
                    }
                    options={designRuleRecommendationTypes.map((t) => ({
                      label: t.label,
                      value: t.id
                    }))}
                    preventDeselection
                    size='sm'
                  />
                </div>
              </div>
              <details className='group'>
                <summary className='flex cursor-pointer items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700'>
                  <ChevronRightIcon className='h-3.5 w-3.5 group-open:hidden' />
                  <ChevronDownIcon className='hidden h-3.5 w-3.5 group-open:block' />
                  AI Instructions
                  {rec.aiInstructions?.trim() ? ' (has content)' : ''}
                </summary>
                <div className='mt-1'>
                  <Textarea
                    value={rec.aiInstructions}
                    onChange={(v) =>
                      updateRecommendation(i, 'aiInstructions', v)
                    }
                    placeholder='Instructions for AI when applying this recommendation...'
                    rows={3}
                  />
                </div>
              </details>
            </div>
          ))}
          <Button
            text='Add Recommendation'
            color='secondary'
            size='md'
            onClick={() =>
              setRecommendations((prev) => [
                ...prev,
                {
                  recommendation: '',
                  aiInstructions: '',
                  active: true,
                  typeId: designRuleRecommendationTypes[0]?.id ?? 1
                }
              ])
            }
            icon={PlusIcon}
          />
        </div>

        <div className='flex items-center gap-3 border-t border-gray-200 pt-4'>
          <Button
            text={isCreating ? 'Create Rule' : 'Save Changes'}
            color='green'
            onClick={handleSave}
            disabled={!code.trim() || !name.trim() || !category.trim()}
          />
          <Button text='Reset' color='white' onClick={handleReset} />
          {!isCreating && selectedRuleId && (
            <div className='ml-auto'>
              <Button text='Delete Rule' color='red' onClick={handleDelete} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className='mx-auto flex max-w-[1600px] flex-col gap-5'>
      <div className='text-3xl font-bold text-gray-900'>
        System Design Rules
      </div>

      <div className='flex items-end gap-3'>
        <div className='w-72'>
          <Text
            value={searchQuery}
            onChange={(v) => setSearchQuery(v)}
            placeholder='Search rules...'
            controlled
          />
        </div>
        <div className='w-72'>
          <Select
            value={categoryFilter}
            onChange={(v) => setCategoryFilter(v.toString())}
            options={[
              { label: 'All Categories', value: '' },
              ...categories.map((c) => ({ label: c, value: c }))
            ]}
          />
        </div>
        <div className='w-56'>
          <Select
            value={stepFilter}
            onChange={(v) => setStepFilter(v.toString())}
            options={[
              { label: 'All Steps', value: '' },
              ...designRuleStepOptions.map((s) => ({ label: s, value: s }))
            ]}
          />
        </div>
        <div className='w-40'>
          <Select
            value={activeFilter}
            onChange={(v) => setActiveFilter(v.toString())}
            options={[
              { label: 'All Status', value: '' },
              { label: 'Active', value: 'active' },
              { label: 'Inactive', value: 'inactive' }
            ]}
          />
        </div>
        <div className='ml-auto'>
          <Button
            text='New Rule'
            color='green'
            onClick={handleNewRule}
            icon={PlusIcon}
          />
        </div>
      </div>

      <div className='max-h-[65vh] overflow-auto'>
        <table className='min-w-full border-separate border-spacing-0 divide-y divide-gray-300'>
          <thead>
            <tr>
              {[
                'Name',
                'Category',
                'Severity',
                'Active',
                'Message',
                'Steps',
                'Fixes'
              ].map((header) => (
                <th
                  key={header}
                  scope='col'
                  className='sticky top-0 z-10 border-b border-gray-300 bg-gray-100 px-3 pb-3.5 pt-3.5 text-left text-sm font-semibold text-gray-900'
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-200'>
            {filteredRules.map((rule) => (
              <tr
                key={rule.id}
                onClick={() => handleSelectRule(rule.id)}
                className='cursor-pointer hover:bg-gray-50'
              >
                <td className='max-w-[300px] truncate px-3 py-3 text-sm font-medium text-gray-900'>
                  {rule.name}
                </td>
                <td className='whitespace-nowrap px-3 py-3 text-sm text-gray-500'>
                  {rule.category}
                </td>
                <td className='whitespace-nowrap px-3 py-3 text-sm'>
                  {severityBadge(rule.severity)}
                </td>
                <td className='whitespace-nowrap px-3 py-3 text-sm'>
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                      rule.active
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {rule.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className='max-w-[350px] truncate px-3 py-3 text-sm text-gray-500'>
                  {rule.message}
                </td>
                <td className='whitespace-nowrap px-3 py-3 text-center text-sm text-gray-500'>
                  {rule.steps.length}
                </td>
                <td className='whitespace-nowrap px-3 py-3 text-center text-sm text-gray-500'>
                  {rule.recommendations.length}
                </td>
              </tr>
            ))}
            {filteredRules.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  className='py-8 text-center text-sm text-gray-400'
                >
                  No rules found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className='text-sm text-gray-400'>
        {filteredRules.length} rule{filteredRules.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}
